/**
 * Profile Menu Dropdown Component
 *
 * @module modules/user/presentation/shared/components/ProfileMenuDropdown
 *
 * Dropdown menu for user profile actions.
 * Supports collapsed/mobile mode with icons only.
 */

import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { memo } from 'react';

import { ProfileMenuItemRow } from './ProfileMenuItemRow';

import type { ProfileMenuDropdownProps } from '../types';

/**
 * Profile Menu Dropdown Component
 *
 * Renders a dropdown menu positioned above the anchor element.
 * Supports both expanded and collapsed (icon-only) modes.
 *
 * @example
 * ```tsx
 * <ProfileMenuDropdown
 *   open={isOpen}
 *   anchorEl={buttonRef.current}
 *   items={menuItems}
 *   collapsed={isMobile}
 *   onItemClick={handleItemClick}
 * />
 * ```
 */
export const ProfileMenuDropdown = memo(function ProfileMenuDropdown({
  open,
  anchorEl,
  items,
  collapsed = false,
  isDark = false,
  isLoggingOut = false,
  onItemClick,
}: ProfileMenuDropdownProps) {
  const muiTheme = useMuiTheme();

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top-start"
      transition
      disablePortal
      sx={{
        zIndex: muiTheme.zIndex.modal,
        // In collapsed mode, use fixed width; otherwise fill available space
        width: collapsed ? 'auto' : 'calc(100% - 16px)',
        minWidth: collapsed ? 48 : 180,
        ml: 1,
        mb: 1,
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {items.map((item, index) => (
              <ProfileMenuItemRow
                key={item.label}
                item={item}
                collapsed={collapsed}
                isLast={index === items.length - 1}
                isDark={isDark}
                isLoggingOut={isLoggingOut}
                onClick={(e) => onItemClick(item, e)}
              />
            ))}
          </Paper>
        </Fade>
      )}
    </Popper>
  );
});

